import { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { deviceType, isMobile } from 'react-device-detect'
import {
  handleCheckoutError,
  isEmpty,
  makeCheckError,
  makeDeviceType,
} from '@open-tender/utils'
import {
  resetCheck,
  resetConfirmation,
  resetErrors,
  selectBrand,
  selectCartTotal,
  selectCheckout,
  selectConfirmationOrder,
  selectContent,
  selectCustomer,
  selectMenuSlug,
  selectOrder,
  setConfirmationOrder,
  setDeviceType,
  setSubmitting,
  validateOrder,
  useAppDispatch,
  useAppSelector,
  fetchLocation,
  openModal,
  updateForm,
  updateCheckoutCustomer,
  resetOrder,
  resetGroupOrder,
  selectConfig,
  resetQuote,
  selectQuote,
  selectSidebar,
  toggleSidebar,
} from '@open-tender/cloud'
import { CustomerCreate } from '@open-tender/types'
import { FormError, Content, Main } from 'components'
import { sendOrderCompleted } from 'app/analytics'
import CheckoutCancelEdit from './CheckoutCancelEdit'
import CheckoutCustomer from './CheckoutCustomer'
import CheckoutGuest from './CheckoutGuest'
import CheckoutPickup from './CheckoutPickup'
import CheckoutDelivery from './CheckoutDelivery'
import CheckoutCart from './CheckoutCart'
import CheckoutHeader from './CheckoutHeader'
import CheckoutAddress from './CheckoutAddress'
import CheckoutDetails from './CheckoutDetails'
import CheckoutCurbside from './CheckoutCurbside'
import CheckoutSurcharges from './CheckoutSurcharges'
import CheckoutDiscounts from './CheckoutDiscounts'
import CheckoutPromoCodes from './CheckoutPromoCodes'
import CheckoutTip from './CheckoutTip'
import CheckoutGiftCards from './CheckoutGiftCards'
import CheckoutTenders from './CheckoutTenders'
import CheckoutSection from './CheckoutSection'
import CheckoutSubmit from './CheckoutSubmit'
import {
  makeAddressError,
  makeCustomerError,
  makeFormTitle,
  makeTipsError,
} from './Checkout.utils'
import {
  CheckoutContent,
  CheckoutInfo,
  CheckoutSidebar,
  CheckoutSidebarContent,
  CheckoutTitle,
  CheckoutView,
} from './Checkout.styled'
import CheckoutValutec from './CheckoutValutec'
import { sendCheckoutView } from 'app/analytics'
import { initKount, isFarmerBoysBrand } from 'utils/helpers'
import TiktokPixel from 'tiktok-pixel'

const Checkout = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {
    title,
    has_promo_codes,
    has_gift_cards,
    has_valutec,
    has_payment_link,
    url,
  } = useAppSelector(selectBrand) || {}
  const { settings, brand: configBrand } = useAppSelector(selectConfig) || {}
  const kount = settings?.kount
  const { checkout: config } = useAppSelector(selectContent) || {}
  const cartTotal = useAppSelector(selectCartTotal)
  const menuSlug = useAppSelector(selectMenuSlug)
  const { serviceType, revenueCenter, requestedAt, cart } =
    useAppSelector(selectOrder)
  const { revenue_center_id: revenueCenterId } = revenueCenter || {}
  const { auth, profile } = useAppSelector(selectCustomer)
  const hasCustomer = !!auth
  const { check, form, errors, submitting, completedOrder } =
    useAppSelector(selectCheckout)
  const hasGiftCards =
    has_gift_cards && check?.config.tender_types.includes('GIFT_CARD')
  const hasValutec =
    has_valutec && check?.config.tender_types.includes('GIFT_CARD')
  const confirmedOrder = useAppSelector(selectConfirmationOrder)
  const hasConfirmationOrder = !!confirmedOrder
  const hasCheck = !!check
  const hasFormCustomer = !isEmpty(form.customer)
  const quote = useAppSelector(selectQuote)
  const { isOpen: isSidebarOpen } = useAppSelector(selectSidebar) || {}
  const formError = errors ? (errors.form as string) || null : null
  const customerError = makeCustomerError(errors)
  const orderCreateError = (errors?.order_id as string) || null
  const checkoutError = customerError || orderCreateError || formError
  const addressError = makeAddressError(errors)
  const tipsError = makeTipsError(errors)
  const deviceTypeName = makeDeviceType(deviceType)
  const { formTitle, hasAddress } = makeFormTitle(check, serviceType)
  const checkError = check?.errors ? makeCheckError(check.errors) : null
  const errMessages = checkError ? handleCheckoutError(checkError) : {}
  const unknownError = (errors?.unknown as string) || null
  const cartError = (errMessages.cart as string) || null
  // requested_at.closing_time_exceeded from the backend guardrail routes
  // through the same $.requested_at param the app already surfaces for
  // other requested-time errors, so no new error-handling plumbing is
  // needed here.
  const pastCloseError = (errMessages.requested_at as string) || null
  const isMissingNames = auth && (!profile?.first_name || !profile.last_name)
  const isQuoteOrder = Boolean(quote.order)

  const isPaymentLink = has_payment_link && isQuoteOrder
  const [mlSessionId, setMlSessionId] = useState<string | null>(null)

  // ml session_id
  useEffect(() => {
    const _sessionid = window.localStorage.getItem('ml_session_id')
    if (_sessionid) setMlSessionId(_sessionid)
  }, [])

  // Accepting a quote should land the customer straight on checkout — the
  // cart sidebar should never be showing here, whether it was already open
  // or something else pops it open on the way in.
  useEffect(() => {
    if (isQuoteOrder && isSidebarOpen) {
      dispatch(toggleSidebar())
    }
  }, [isQuoteOrder, isSidebarOpen, dispatch])

  useEffect(() => {
    if (!kount || !kount.enabled) return
    const { clientId, environment } = kount
    initKount(clientId, environment, ({ SessionID }) =>
      dispatch(updateForm({ kount_device_session_id: SessionID }))
    )
  }, [dispatch, kount])

  useEffect(() => {
    if (!submitting && formError) window.scrollTo(0, 0)
  }, [formError, submitting])

  // Not on the ConfigBrand type in the installed @open-tender/types tarball.
  const sendDynamicPrepTime = Boolean(
    (configBrand as { send_dynamic_prep_time?: boolean } | undefined)
      ?.send_dynamic_prep_time
  )
  // With SEND_DYNAMIC_PREP_TIME the server floors the pickup time itself
  // (DO-230). Refetching with the cart total would let setRevenueCenter snap
  // the customer's pick to the tier-inflated first slot before validate runs,
  // so those brands fetch once, without the cart total. Delivery is outside
  // DO-230 and its ASAP wait still needs the tier from the cart total.
  const locationCartTotal =
    sendDynamicPrepTime && serviceType !== 'DELIVERY'
      ? undefined
      : cartTotal.toFixed(2)
  useEffect(() => {
    if (revenueCenterId) {
      dispatch(fetchLocation({ revenueCenterId, cartTotal: locationCartTotal }))
    }
  }, [revenueCenterId, locationCartTotal, dispatch])

  useEffect(() => {
    dispatch(setSubmitting(false))
    dispatch(resetConfirmation())
    dispatch(setDeviceType(deviceTypeName))
    return () => {
      dispatch(resetErrors())
      dispatch(resetCheck())
    }
  }, [dispatch, deviceTypeName])

  useEffect(() => {
    if (mlSessionId) dispatch(updateForm({ session_id: mlSessionId }))
  }, [dispatch, mlSessionId])

  const checkLoaded = check && Boolean(check.cart)
  useEffect(() => {
    if (checkLoaded) sendCheckoutView(check)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkLoaded])

  const updateCustomer = useCallback(
    async (data: CustomerCreate) => {
      const { first_name, last_name, email, phone, company } = data
      const customer = {
        customer_id: profile?.customer_id,
        first_name,
        last_name,
        email,
        phone,
        company,
      }
      await dispatch(updateForm({ customer }))
      await dispatch(updateCheckoutCustomer({ customer }))
      dispatch(validateOrder())
    },
    [dispatch, profile?.customer_id]
  )

  const openUpdateCustomerModal = useCallback(() => {
    dispatch(openModal({ type: 'profile', args: { callback: updateCustomer } }))
  }, [dispatch, updateCustomer])

  // revenueCenterId/serviceType/requestedAt/cart are included so that if the
  // first attempt runs before they're all settled (assembleOrder returns
  // null, surfacing "Empty cart. Please return to menu."), this retries once
  // they're populated instead of leaving the customer stuck on that error.
  useEffect(() => {
    if (isMissingNames) {
      openUpdateCustomerModal()
      return
    }
    if (!completedOrder && hasFormCustomer && !hasCheck) {
      dispatch(validateOrder())
    }
  }, [
    dispatch,
    completedOrder,
    hasFormCustomer,
    hasCheck,
    isMissingNames,
    openUpdateCustomerModal,
    revenueCenterId,
    serviceType,
    requestedAt,
    cart,
  ])

  useEffect(() => {
    if (isMissingNames) {
      openUpdateCustomerModal()
      return
    }
    if (hasCheck) {
      dispatch(validateOrder())
    }
  }, [dispatch, hasCheck, isMissingNames, openUpdateCustomerModal, requestedAt])

  useEffect(() => {
    const confirmOrder = async () => {
      dispatch(setConfirmationOrder(completedOrder))
      if (completedOrder?.order_id) {
        sendOrderCompleted({ order_id: completedOrder.order_id })
      }
      await dispatch(resetOrder())
      await dispatch(resetQuote())
      await dispatch(resetGroupOrder())

      if (checkLoaded && isFarmerBoysBrand) {
        TiktokPixel.track('Purchase', {
          contents: check.cart?.map((item) => {
            return {
              price: item.price_total,
            }
          }),
        })
      }

      navigate(`/confirmation${isQuoteOrder ? '?is_quote=true' : ''}`)
    }
    if (completedOrder) {
      if (!hasConfirmationOrder) {
        confirmOrder()
      }
    } else if (cartTotal === 0) {
      if (has_payment_link && url) {
        window.location.href = url
      } else {
        navigate(menuSlug)
      }
    } else if (!revenueCenterId || !serviceType) {
      navigate('/account')
    } else if (!hasCustomer && !hasFormCustomer) {
      navigate('/checkout/guest')
    }
  }, [
    dispatch,
    navigate,
    cartTotal,
    menuSlug,
    revenueCenterId,
    serviceType,
    completedOrder,
    hasCustomer,
    hasFormCustomer,
    hasConfirmationOrder,
    isQuoteOrder,
    url,
    has_payment_link,
    checkLoaded,
    check?.cart,
  ])

  return (
    <>
      <Helmet>
        <title>Checkout | {title}</title>
        <script
          type="text/javascript"
          src="https://pg.feroot.com/v1/bundle/9e70ab68-af2d-4de8-a7f2-5b518104089a"
        />
      </Helmet>
      <Content hasRouter={!isPaymentLink}>
        <CheckoutHeader />
        <Main bgColor="checkout" style={{ overflow: 'hidden' }}>
          <CheckoutView>
            <CheckoutContent>
              <CheckoutTitle>
                <h1>{config?.title}</h1>
                <p>{config?.subtitle}</p>
                {!isPaymentLink && <CheckoutCancelEdit />}
                {checkoutError && <FormError errMsg={checkoutError} />}
                {addressError && <FormError errMsg={addressError} />}
                {cartError && <FormError errMsg={cartError} />}
                {pastCloseError && <FormError errMsg={pastCloseError} />}
                {tipsError && <FormError errMsg={tipsError} />}
                {unknownError && <FormError errMsg={unknownError} />}
              </CheckoutTitle>
              <CheckoutInfo>
                {auth ? <CheckoutCustomer /> : <CheckoutGuest />}
                {check && (
                  <>
                    {['WALKIN', 'PICKUP'].includes(serviceType ?? 'WALKIN') ? (
                      <CheckoutPickup />
                    ) : serviceType === 'DELIVERY' ? (
                      <CheckoutDelivery />
                    ) : null}
                  </>
                )}
              </CheckoutInfo>
              {!isPaymentLink && formTitle && (
                <CheckoutSection title={formTitle} style={{ padding: '0' }}>
                  <CheckoutAddress />
                  <CheckoutDetails hasAddress={hasAddress} />
                </CheckoutSection>
              )}
              {check ? (
                !isPaymentLink ? (
                  <>
                    <CheckoutCurbside setInitialValue={!formTitle} />
                    <CheckoutSurcharges />
                    <CheckoutDiscounts />
                    {has_promo_codes && <CheckoutPromoCodes />}
                    <CheckoutTip />
                    {isMobile && (
                      <CheckoutCart
                        highlightServiceFee={config?.highlightServiceFee}
                      />
                    )}
                    {hasGiftCards && <CheckoutGiftCards />}
                    {hasValutec && <CheckoutValutec />}
                    <CheckoutTenders />
                    <CheckoutSubmit paymentLink={isPaymentLink} />
                  </>
                ) : (
                  <>
                    <CheckoutTip />
                    {isMobile && (
                      <CheckoutCart
                        highlightServiceFee={config?.highlightServiceFee}
                      />
                    )}
                    <CheckoutTenders />
                    <CheckoutSubmit paymentLink={isPaymentLink} />
                  </>
                )
              ) : null}
            </CheckoutContent>
            <CheckoutSidebar>
              <CheckoutSidebarContent>
                {!isMobile && check && (
                  <CheckoutCart
                    highlightServiceFee={config?.highlightServiceFee}
                  />
                )}
              </CheckoutSidebarContent>
            </CheckoutSidebar>
          </CheckoutView>
        </Main>
      </Content>
    </>
  )
}

export default Checkout
